import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import type { FormPage } from '../../models/built-form';
import { sortedPages } from '../../utils/form-pages';

@Component({
  selector: 'app-form-page-pager',
  standalone: true,
  templateUrl: './form-page-pager.html',
  styleUrl: './form-page-pager.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormPagePagerComponent {
  readonly pages = input<FormPage[]>([]);
  readonly activePageId = input<string | null>(null);
  readonly activePageIdChange = output<string>();
  readonly addPage = output<void>();
  readonly openPagesManager = output<void>();

  readonly orderedPages = computed(() => sortedPages(this.pages()));

  readonly activeIndex = computed(() => {
    const pages = this.orderedPages();
    const id = this.activePageId();
    const index = pages.findIndex((page) => page.id === id);
    return index >= 0 ? index : 0;
  });

  readonly pageCount = computed(() => this.orderedPages().length);

  readonly label = computed(() => {
    const count = this.pageCount();
    if (count === 0) {
      return 'Page 0 of 0';
    }
    return `Page ${this.activeIndex() + 1} of ${count}`;
  });

  readonly canGoPrev = computed(() => this.activeIndex() > 0);
  readonly canGoNext = computed(
    () => this.activeIndex() < this.pageCount() - 1,
  );

  goPrev(event: Event): void {
    event.stopPropagation();
    if (!this.canGoPrev()) {
      return;
    }
    const page = this.orderedPages()[this.activeIndex() - 1];
    if (page) {
      this.activePageIdChange.emit(page.id);
    }
  }

  goNext(event: Event): void {
    event.stopPropagation();
    if (!this.canGoNext()) {
      return;
    }
    const page = this.orderedPages()[this.activeIndex() + 1];
    if (page) {
      this.activePageIdChange.emit(page.id);
    }
  }

  onOpenPagesManager(event: Event): void {
    event.stopPropagation();
    this.openPagesManager.emit();
  }

  onAddPage(event: Event): void {
    event.stopPropagation();
    this.addPage.emit();
  }
}
